/**
 * Organization Management Store
 *
 * Zustand store for org management state: current user context,
 * org users, departments, reporting structure.
 */
import { create } from 'zustand';
import type {
  CurrentUserContext, OrgUser, Department, OrgTreeNode,
} from '../services/api/org';
import * as orgApi from '../services/api/org';

const _orgInflight: Record<string, Promise<void>> = {};

interface OrgState {
  // Current user context (enriched auth)
  userContext: CurrentUserContext | null;
  contextLoading: boolean;
  contextError: string | null;

  // Organization users
  users: OrgUser[];
  usersLoading: boolean;

  // Departments
  departments: Department[];
  departmentsLoading: boolean;

  // Org tree (reporting structure)
  orgTree: OrgTreeNode[];
  orgTreeLoading: boolean;

  // Active management view
  activeOrgView: 'users' | 'departments' | 'structure' | 'settings';
  selectedUserId: string | null;
}

interface OrgActions {
  // Context
  fetchUserContext: () => Promise<void>;
  clearContext: () => void;

  // Users
  fetchUsers: () => Promise<void>;
  inviteUser: (data: Parameters<typeof orgApi.inviteUser>[0]) => Promise<OrgUser>;
  updateUser: (userId: string, data: Parameters<typeof orgApi.updateUser>[1]) => Promise<void>;
  deactivateUser: (userId: string) => Promise<void>;

  // Departments
  fetchDepartments: () => Promise<void>;
  addDepartment: (name: string, parentId?: string) => Promise<void>;
  updateDepartment: (deptId: string, updates: { name?: string; parent_dept_id?: string }) => Promise<void>;
  removeDepartment: (deptId: string) => Promise<void>;

  // Reporting structure
  fetchOrgTree: () => Promise<void>;
  setUserReporting: (userId: string, managerId: string | null) => Promise<void>;

  // Navigation
  setActiveOrgView: (view: OrgState['activeOrgView']) => void;
  setSelectedUserId: (userId: string | null) => void;
}

// ── Tree manipulation helpers (optimistic updates) ──

/** Remove a node by ID from anywhere in the tree, returning the pruned tree and the removed node (with its subtree intact). */
function removeNodeFromTree(
  nodes: OrgTreeNode[],
  targetId: string,
): [OrgTreeNode[], OrgTreeNode | null] {
  let removed: OrgTreeNode | null = null;
  const result: OrgTreeNode[] = [];
  for (const node of nodes) {
    if (node.id === targetId) {
      removed = node;
    } else if (!removed) {
      const [newChildren, found] = removeNodeFromTree(node.reports, targetId);
      if (found) {
        removed = found;
        result.push({ ...node, reports: newChildren });
      } else {
        result.push(node);
      }
    } else {
      result.push(node);
    }
  }
  return [result, removed];
}

/** Insert a node as a child of parentId. */
function insertNodeUnder(
  nodes: OrgTreeNode[],
  parentId: string,
  child: OrgTreeNode,
): OrgTreeNode[] {
  return nodes.map((node) => {
    if (node.id === parentId) {
      return { ...node, reports: [...node.reports, child] };
    }
    const updated = insertNodeUnder(node.reports, parentId, child);
    return updated !== node.reports ? { ...node, reports: updated } : node;
  });
}

/** Convert an OrgUser to a leaf OrgTreeNode. */
function userToTreeNode(users: OrgUser[], userId: string): OrgTreeNode | null {
  const u = users.find((x) => x.id === userId);
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    display_name: u.display_name,
    title: u.title,
    system_role: u.system_role,
    department_id: u.department_id,
    reports: [],
  };
}

export const useOrgStore = create<OrgState & OrgActions>((set, get) => ({
  // ── State ──
  userContext: null,
  contextLoading: false,
  contextError: null,
  users: [],
  usersLoading: false,
  departments: [],
  departmentsLoading: false,
  orgTree: [],
  orgTreeLoading: false,
  activeOrgView: 'users',
  selectedUserId: null,

  // ── Context ──
  fetchUserContext: () => {
    if (get().userContext) return Promise.resolve();
    if (_orgInflight['context']) return _orgInflight['context'];
    _orgInflight['context'] = (async () => {
      set({ contextLoading: true, contextError: null });
      try {
        const ctx = await orgApi.getMyContext();
        set({ userContext: ctx, contextLoading: false });
        if (ctx.theme_config) {
          const { useThemeStore } = await import('./themeStore');
          useThemeStore.getState().setOrgTheme(ctx.theme_config);
        }
      } catch (err: any) {
        const message = err?.response?.data?.detail || 'Failed to load user context';
        set({ contextError: message, contextLoading: false });
      } finally {
        delete _orgInflight['context'];
      }
    })();
    return _orgInflight['context'];
  },

  clearContext: () => set({
    userContext: null,
    contextError: null,
    users: [],
    departments: [],
    orgTree: [],
  }),

  // ── Users ──
  fetchUsers: () => {
    if (get().users.length > 0) return Promise.resolve();
    if (_orgInflight['users']) return _orgInflight['users'];
    _orgInflight['users'] = (async () => {
      set({ usersLoading: true });
      try {
        const users = await orgApi.listOrgUsers();
        set({ users, usersLoading: false });
      } catch {
        set({ usersLoading: false });
      } finally {
        delete _orgInflight['users'];
      }
    })();
    return _orgInflight['users'];
  },

  inviteUser: async (data) => {
    const newUser = await orgApi.inviteUser(data);
    set((s) => ({ users: [...s.users, newUser], orgTree: [] }));
    await get().fetchOrgTree();
    return newUser;
  },

  updateUser: async (userId, data) => {
    const updated = await orgApi.updateUser(userId, data);
    set((s) => ({
      users: s.users.map((u) => (u.id === userId ? updated : u)),
    }));
  },

  deactivateUser: async (userId) => {
    await orgApi.deactivateUser(userId);
    set((s) => ({
      users: s.users.filter((u) => u.id !== userId),
    }));
  },

  // ── Departments ──
  fetchDepartments: () => {
    if (get().departments.length > 0) return Promise.resolve();
    if (_orgInflight['departments']) return _orgInflight['departments'];
    _orgInflight['departments'] = (async () => {
      set({ departmentsLoading: true });
      try {
        const departments = await orgApi.listDepartments();
        set({ departments, departmentsLoading: false });
      } catch {
        set({ departmentsLoading: false });
      } finally {
        delete _orgInflight['departments'];
      }
    })();
    return _orgInflight['departments'];
  },

  addDepartment: async (name, parentId) => {
    const dept = await orgApi.createDepartment({
      name,
      parent_dept_id: parentId,
    });
    set((s) => ({ departments: [...s.departments, dept] }));
  },

  updateDepartment: async (deptId, updates) => {
    const updated = await orgApi.updateDepartment(deptId, updates);
    set((s) => ({
      departments: s.departments.map((d) => (d.id === deptId ? updated : d)),
    }));
  },

  removeDepartment: async (deptId) => {
    await orgApi.deleteDepartment(deptId);
    set((s) => ({
      departments: s.departments.filter((d) => d.id !== deptId),
    }));
  },

  // ── Reporting ──
  fetchOrgTree: () => {
    if (get().orgTree.length > 0) return Promise.resolve();
    if (_orgInflight['orgTree']) return _orgInflight['orgTree'];
    _orgInflight['orgTree'] = (async () => {
      set({ orgTreeLoading: true });
      try {
        const tree = await orgApi.getOrgTree();
        set({ orgTree: tree, orgTreeLoading: false });
      } catch {
        set({ orgTreeLoading: false });
      } finally {
        delete _orgInflight['orgTree'];
      }
    })();
    return _orgInflight['orgTree'];
  },

  setUserReporting: async (userId, managerId) => {
    const { orgTree, users } = get();

    // ── Optimistic local update (instant, no loading state) ──
    const [treeWithout, removedNode] = removeNodeFromTree(orgTree, userId);
    let optimisticTree: OrgTreeNode[];
    if (managerId) {
      const nodeToInsert = removedNode || userToTreeNode(users, userId);
      optimisticTree = nodeToInsert
        ? insertNodeUnder(treeWithout, managerId, nodeToInsert)
        : treeWithout;
    } else {
      // Unassign — just remove from tree
      optimisticTree = treeWithout;
    }
    set({
      orgTree: optimisticTree,
      users: users.map((u) => (u.id === userId ? { ...u, reports_to: managerId } : u)),
    });

    // ── Persist to backend (silent, no spinner) ──
    try {
      await orgApi.setReporting(userId, managerId);
    } catch {
      // Revert on failure — silent full refresh
      const [freshTree, freshUsers] = await Promise.all([
        orgApi.getOrgTree(),
        orgApi.listOrgUsers(),
      ]);
      set({ orgTree: freshTree, users: freshUsers });
    }
  },

  // ── Navigation ──
  setActiveOrgView: (view) => set({ activeOrgView: view }),
  setSelectedUserId: (userId) => set({ selectedUserId: userId }),
}));
